/**
 * Cuánto nos cuesta de verdad cada envío de WhatsApp oficial, según lo que facturó Twilio en las fechas elegidas.
 * Pedido de Alan del 12/10/2026: «que en la plataforma diga el costo real que venimos gastando por envío».
 *
 * Arriba, el número: todo lo de WhatsApp que facturó Twilio dividido por los mensajes que salieron. Abajo, por tipo
 * de mensaje, lo real (lo que cobra Meta + lo que cobra Twilio) contra nuestra tabla, que es con lo que se le cobra a
 * la marca: si Meta o Twilio cambian el precio, acá se ve. Sólo para el equipo (lo de Twilio no le llega a un cliente).
 *
 * La comparación sólo avisa con una muestra que alcance: con 8 plantillas en HATSU (12/10), Twilio dijo «1» y la
 * primera versión avisaba que la tabla cobraba de menos cuando cobraba de más.
 */
import type { CostoPorEnvio, CostoPorTipo, EstadisticasTwilio } from "../../types.estadisticas";
import { usd, usdPorMensaje } from "../../logic/formato";
import { pctConSigno } from "../../logic/estadisticas";
import { num } from "../../../campana-nueva/utils/format";
import { AvisoConectar, Vacio } from "./comun";

/** Una diferencia de menos de 2 % es ruido de redondeo de Twilio, no un cambio de precio. */
export const UMBRAL_DIFERENCIA = 0.02;
/** Con menos mensajes de un tipo la diferencia no dice nada todavía: una plantilla a otro país mueve el promedio. */
export const MUESTRA_MINIMA = 20;

const detalle = (t: CostoPorTipo) => `${t.etiqueta}: sale ${usdPorMensaje(t.realPorMensajeUSD)} y nuestra tabla cobra ${usdPorMensaje(t.tablaPorMensajeUSD)}`;

export function TarjetaCostoPorEnvio({ costo, twilio }: {
  costo: CostoPorEnvio | null | undefined;
  /** sin cuenta disponible, lo mismo que dicen las demás tarjetas de Twilio: que hay que conectarla */
  twilio?: Pick<EstadisticasTwilio, "disponible" | "motivoNoDisponible">;
}) {
  if (twilio && !twilio.disponible) return <AvisoConectar motivo={twilio.motivoNoDisponible} />;
  if (!costo || !costo.hayDatos) {
    return <Vacio>Twilio todavía no informó lo que facturó en estas fechas. Tocá «Actualizar datos» para traerlo.</Vacio>;
  }
  const conDiferencia = costo.tipos.filter((t) => t.diferencia !== null && Math.abs(t.diferencia) >= UMBRAL_DIFERENCIA);
  const firme = (t: CostoPorTipo) => t.mensajes >= MUESTRA_MINIMA;
  const masCaro = conDiferencia.filter((t) => firme(t) && (t.diferencia ?? 0) > 0);
  const masBarato = conDiferencia.filter((t) => firme(t) && (t.diferencia ?? 0) < 0);
  const pocos = conDiferencia.filter((t) => !firme(t));
  const sinClasificar = costo.sinClasificar || [];

  return (
    <div className="space-y-4" data-testid="costo-por-envio">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="rounded-xl bg-violet-50 px-3 py-3">
          <p className="text-2xl font-bold text-violet-800 tabular-nums" data-testid="costo-por-envio-valor">{usdPorMensaje(costo.porEnvioUSD)}</p>
          <p className="text-xs text-violet-900">por envío, todo incluido</p>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-3">
          <p className="text-lg font-semibold text-slate-900 tabular-nums">{usd(costo.gastoUSD)}</p>
          <p className="text-xs text-slate-600">gastado en WhatsApp</p>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-3">
          <p className="text-lg font-semibold text-slate-900 tabular-nums">{num(costo.enviados)}</p>
          <p className="text-xs text-slate-600">mensajes enviados</p>
          {costo.enviadosSegunTwilio !== costo.enviados && (
            <p className="text-[11px] text-slate-400" data-testid="enviados-segun-twilio">Twilio cobró {num(costo.enviadosSegunTwilio)} como salientes</p>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">
        Es todo lo de WhatsApp que facturó Twilio en estas fechas (lo de Meta por cada plantilla, lo de Twilio por cada mensaje
        {costo.recibidos > 0 ? `, y ${usd(costo.recibidosUSD)} por ${num(costo.recibidos)} mensajes recibidos` : ""}), dividido por los
        mensajes que salieron de la plataforma (campañas, respuestas desde Conversaciones y automáticas).
        {costo.twilioPorEnvioUSD !== null && (costo.twilioDeLaTabla
          ? ` El cargo de Twilio por mensaje no vino en su facturación: para lo real por tipo se usa el de nuestra tabla (${usdPorMensaje(costo.twilioPorEnvioUSD)}).`
          : costo.twilioPorEnvioUSD === 0
            ? " Twilio no cobró cargo por mensaje en estas fechas: su facturación lo informa en 0."
            : ` De eso, Twilio cobra ${usdPorMensaje(costo.twilioPorEnvioUSD)} por cada mensaje que sale.`)}
        {" "}Twilio puede tardar en informar lo facturado: los últimos días pueden quedar cortos.
      </p>

      {masCaro.length > 0 && (
        <p className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
          {masCaro.map(detalle).join(". ")}.
          {" "}Nuestra tabla está por debajo de lo real: la diferencia sale del recargo que se le cobra a la marca (o de Deenex, si el recargo no alcanza).
        </p>
      )}
      {masBarato.length > 0 && (
        <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700" role="note" data-testid="tabla-por-encima">
          {masBarato.map(detalle).join(". ")}.
          {" "}Meta y Twilio cobraron menos de lo que estima nuestra tabla: a la marca se le descuenta más de lo que cuesta, y esa diferencia queda para Deenex además del recargo.
        </p>
      )}
      {pocos.length > 0 && (
        <p className="text-xs text-slate-500" data-testid="muestra-chica">
          Con pocos mensajes la comparación con la tabla todavía no es firme:{" "}
          {pocos.map((t) => `${t.etiqueta} (${num(t.mensajes)})`).join(", ")}. Hacen falta al menos {MUESTRA_MINIMA} de cada tipo.
        </p>
      )}

      {costo.tipos.length > 0 && (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full min-w-[560px] text-sm">
            <caption className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 px-2">Por tipo de mensaje</caption>
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                <th scope="col" className="px-2 py-2">Tipo</th>
                <th scope="col" className="px-2 py-2 text-right">Mensajes</th>
                <th scope="col" className="px-2 py-2 text-right">Meta</th>
                <th scope="col" className="px-2 py-2 text-right">Real por mensaje</th>
                <th scope="col" className="px-2 py-2 text-right">Nuestra tabla</th>
                <th scope="col" className="px-2 py-2 text-right">Diferencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {costo.tipos.map((t) => {
                const dif = t.diferencia === null ? null : pctConSigno(t.diferencia * 100);
                const alerta = masCaro.includes(t);
                const otraCuenta = typeof t.mensajesSegunTwilio === "number" && t.mensajesSegunTwilio !== t.mensajes;
                return (
                  <tr key={t.grupo}>
                    <td className="px-2 py-2 text-slate-800">{t.etiqueta}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {num(t.mensajes)}
                      {otraCuenta && <span className="block text-[11px] text-slate-400">Twilio: {num(t.mensajesSegunTwilio ?? 0)}</span>}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{usdPorMensaje(t.metaPorMensajeUSD)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold text-slate-900">{usdPorMensaje(t.realPorMensajeUSD)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{usdPorMensaje(t.tablaPorMensajeUSD)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${alerta ? "font-semibold text-amber-700" : "text-slate-500"}`}>{dif ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-1 px-2 text-[11px] text-slate-400">
            «Mensajes» son las plantillas que entregó la plataforma, que es lo que cobra Meta (si Twilio contó otra cantidad, va
            abajo). «Real por mensaje» = lo que cobró Meta por ese tipo dividido por esos mensajes, más el cargo de Twilio por
            mensaje. «Nuestra tabla» es lo que estima la plataforma por mensaje entregado, y con eso (más el recargo) se descuenta
            del saldo de la marca.
          </p>
        </div>
      )}
      {sinClasificar.length > 0 && (
        <p className="text-xs text-slate-500" data-testid="sin-clasificar">
          Twilio también facturó en otras categorías de WhatsApp que esta cuenta no suma:{" "}
          {sinClasificar.map((x) => `${x.categoria} (${usdPorMensaje(x.usd)})`).join(", ")}.
        </p>
      )}
      {costo.otraMoneda > 0 && (
        <p className="text-xs text-slate-500">{num(costo.otraMoneda)} {costo.otraMoneda === 1 ? "importe vino" : "importes vinieron"} en otra moneda y no se sumaron.</p>
      )}
    </div>
  );
}

export default TarjetaCostoPorEnvio;
